import { create } from 'zustand'

import { api, errorMessage } from '@/lib/api'
import { type AsyncState, failed, idle, loaded, loading } from '@/lib/async-state'
import type {
  Page,
  WhitelistApplication,
  WhitelistApplicationDetail,
  WhitelistCheck,
  WhitelistCheckReport,
  WhitelistStats,
  WhitelistStatus,
} from '@/types/api'

export interface WhitelistFilters {
  status?: WhitelistStatus[]
  search?: string
  flagged_only?: boolean
  page: number
  page_size: number
}

const OPEN: WhitelistStatus[] = ['pending', 'in_review', 'check_requested', 'check_submitted']
const DEFAULT_FILTERS: WhitelistFilters = { status: OPEN, page: 1, page_size: 25 }

interface WhitelistState {
  list: AsyncState<Page<WhitelistApplication> | null>
  detail: AsyncState<WhitelistApplicationDetail | null>
  stats: AsyncState<WhitelistStats | null>
  filters: WhitelistFilters
  /** The freshly issued code, held only until the dialog is dismissed. */
  issuedCode: WhitelistCheck | null

  setFilters: (patch: Partial<WhitelistFilters>) => Promise<void>
  loadList: () => Promise<void>
  loadDetail: (id: number) => Promise<void>
  loadStats: () => Promise<void>

  decide: (
    id: number,
    decision: 'approve' | 'decline' | 'postpone',
    payload: { note?: string; notify_member?: boolean },
  ) => Promise<void>
  /** Whitelist somebody with no application to decide. */
  grant: (payload: {
    discord_id: string
    username?: string
    note?: string
    notify_member?: boolean
  }) => Promise<WhitelistApplicationDetail>
  revoke: (id: number, reason: string, removeRole: boolean) => Promise<void>
  requestCheck: (id: number) => Promise<WhitelistCheck>
  cancelCheck: (id: number) => Promise<void>
  clearIssuedCode: () => void
  loadReport: (checkId: number) => Promise<WhitelistCheckReport>
  purgeReport: (checkId: number) => Promise<void>
}

export const OPEN_STATUSES = OPEN

export const useWhitelistStore = create<WhitelistState>((set, get) => ({
  list: idle<Page<WhitelistApplication> | null>(null),
  detail: idle<WhitelistApplicationDetail | null>(null),
  stats: idle<WhitelistStats | null>(null),
  filters: DEFAULT_FILTERS,
  issuedCode: null,

  setFilters: async (patch) => {
    const page = 'page' in patch ? (patch.page as number) : 1
    set({ filters: { ...get().filters, ...patch, page } })
    await get().loadList()
  },

  loadList: async () => {
    set({ list: loading(get().list) })
    try {
      const { status, ...rest } = get().filters
      const data = await api.get<Page<WhitelistApplication>>('/whitelist', {
        query: { ...rest, status: status?.length ? status : undefined },
      })
      set({ list: loaded<Page<WhitelistApplication> | null>(data) })
    } catch (error) {
      set({ list: failed(get().list, errorMessage(error)) })
    }
  },

  loadDetail: async (id) => {
    set({ detail: loading(get().detail) })
    try {
      const data = await api.get<WhitelistApplicationDetail>(`/whitelist/${id}`)
      set({ detail: loaded<WhitelistApplicationDetail | null>(data) })
    } catch (error) {
      set({ detail: failed(get().detail, errorMessage(error)) })
    }
  },

  loadStats: async () => {
    try {
      set({
        stats: loaded<WhitelistStats | null>(await api.get<WhitelistStats>('/whitelist/stats')),
      })
    } catch (error) {
      set({ stats: failed(get().stats, errorMessage(error)) })
    }
  },

  decide: async (id, decision, payload) => {
    const detail = await api.post<WhitelistApplicationDetail>(`/whitelist/${id}/${decision}`, {
      notify_member: true,
      ...payload,
    })
    set({ detail: loaded<WhitelistApplicationDetail | null>(detail) })
    await Promise.all([get().loadList(), get().loadStats()])
  },

  grant: async (payload) => {
    const detail = await api.post<WhitelistApplicationDetail>('/whitelist/grant', {
      notify_member: true,
      ...payload,
    })
    // The queue moves too: granting to somebody who had applied decides their
    // application rather than leaving it open.
    await Promise.all([get().loadList(), get().loadStats()])
    return detail
  },

  revoke: async (id, reason, remove_role) => {
    const detail = await api.post<WhitelistApplicationDetail>(`/whitelist/${id}/revoke`, {
      reason,
      remove_role,
      notify_member: true,
    })
    set({ detail: loaded<WhitelistApplicationDetail | null>(detail) })
    await Promise.all([get().loadList(), get().loadStats()])
  },

  requestCheck: async (id) => {
    const check = await api.post<WhitelistCheck>(`/whitelist/${id}/check`)
    // Kept in the store so the dialog can show the command once; the code is
    // never returned again by any later read.
    set({ issuedCode: check })
    await Promise.all([get().loadDetail(id), get().loadStats()])
    return check
  },

  cancelCheck: async (id) => {
    await api.delete(`/whitelist/${id}/check`)
    await Promise.all([get().loadDetail(id), get().loadStats()])
  },

  clearIssuedCode: () => set({ issuedCode: null }),

  loadReport: (checkId) => api.get<WhitelistCheckReport>(`/whitelist/checks/${checkId}/report`),

  purgeReport: async (checkId) => {
    await api.delete(`/whitelist/checks/${checkId}/report`)
    const current = get().detail.data
    if (current) await get().loadDetail(current.id)
  },
}))
